<template>
    <b-form @submit.stop.prevent="saveBillingInfo" class="billing-info-form">
        <h4 class="billing-title">{{ $t('billingInformation') }}</h4>

        <!-- Type toggle -->
        <div class="type-toggle mb-4">
            <button
                type="button"
                class="type-btn"
                :class="{ active: billingAddress.type === 'person' }"
                @click="setBillingType('person')"
            >
                <i class="fa fa-user mr-2"></i>{{ $t('billingAddress.person') }}
            </button>
            <button
                type="button"
                class="type-btn"
                :class="{ active: billingAddress.type === 'company' }"
                @click="setBillingType('company')"
            >
                <i class="fa fa-building mr-2"></i>{{ $t('billingAddress.company') }}
            </button>
        </div>

        <!-- Person -->
        <div v-if="billingAddress.type === 'person'" key="person">
            <SelectField
                name="country"
                :label="$t('country')"
                v-model="billingAddress.country"
                @error="(e) => { fieldError(e, 'personBillingErrors') }"
                :items="countries"
                :reduce="(o) => o"
                :rules="{required:true, length: {min: 2}}"
                v-if="countries"
            />

            <b-row>
                <b-col md="6">
                    <InputField
                        name="firstname"
                        :label="$t('firstname')"
                        v-model="billingAddress.firstname"
                        @error="(e) => { fieldError(e, 'personBillingErrors') }"
                        :rules="{required:true, length: {min: 2}}"
                    />
                </b-col>
                <b-col md="6">
                    <InputField
                        name="lastname"
                        :label="$t('lastname')"
                        v-model="billingAddress.lastname"
                        @error="(e) => { fieldError(e, 'personBillingErrors') }"
                        :rules="{required:true, length: {min: 2}}"
                    />
                </b-col>
            </b-row>

            <InputField
                type="email"
                name="email"
                :label="$t('email')"
                v-model="billingAddress.email"
                @error="(e) => { fieldError(e, 'billingErrors') }"
                :rules="{required:true, length: {min: 2}}"
            />

            <InputField
                name="address"
                :label="$t('address')"
                v-model="billingAddress.address"
                @error="(e) => { fieldError(e, 'personBillingErrors') }"
                :rules="{required:true, length: {min: 2}}"
            />

            <b-row>
                <b-col md="6">
                    <InputField
                        name="city"
                        :label="$t('city')"
                        v-model="billingAddress.city"
                        @error="(e) => { fieldError(e, 'personBillingErrors') }"
                        :rules="{required:true, length: {min: 2}}"
                    />
                </b-col>
                <b-col md="3">
                    <InputField
                        name="zipcode"
                        :label="$t('zipcode')"
                        v-model="billingAddress.zipcode"
                        @error="(e) => { fieldError(e, 'personBillingErrors') }"
                        :rules="{required:false, length: {min: 2}}"
                    />
                </b-col>
                <b-col md="3">
                    <InputField
                        name="state"
                        :label="$t('state')"
                        v-model="billingAddress.state"
                        @error="(e) => { fieldError(e, 'billingErrors') }"
                        :rules="{required:false, length: {min: 2}}"
                    />
                </b-col>
            </b-row>
        </div>

        <!-- Company -->
        <div v-if="billingAddress.type === 'company'" key="company">
            <protokol-invalid-feedback :errors="validator.fieldErrors('country')">
                <b-form-group :label="$t('country')">
                    <protokol-input-select
                        name="country"
                        v-model="billingAddress.country"
                        :options="countries"
                        v-if="countries"
                    />
                </b-form-group>
            </protokol-invalid-feedback>

            <b-row>
                <b-col md="6">
                    <protokol-invalid-feedback :errors="validator.fieldErrors('vat_number')">
                        <b-form-group
                            :label="$t('vat')"
                            :description="billingAddress.country === 'Serbia' && !vatError ? $t('enteringVatNumberAutoSearch') : ''"
                        >
                            <b-input-group v-if="billingAddress.country === 'Serbia'">
                                <b-form-input
                                    name="vat_number"
                                    v-model="billingAddress.vat_number"
                                    maxlength="9"
                                    :state="vatError ? false : null"
                                    @input="onVatInput"
                                />
                                <b-input-group-append>
                                    <b-input-group-text>
                                        <b-spinner small v-if="fetchingVatData"></b-spinner>
                                        <b-icon
                                            v-else-if="billingAddress.vat_number && billingAddress.vat_number.length === 9 && !vatError"
                                            icon="check-circle-fill"
                                            variant="success"
                                        ></b-icon>
                                    </b-input-group-text>
                                </b-input-group-append>
                                <b-form-invalid-feedback :state="!vatError">{{ vatError }}</b-form-invalid-feedback>
                            </b-input-group>
                            <b-form-input v-else name="vat_number" v-model="billingAddress.vat_number" />
                        </b-form-group>
                    </protokol-invalid-feedback>
                </b-col>
                <b-col md="6" v-if="billingAddress.country === 'Serbia'">
                    <protokol-invalid-feedback :errors="validator.fieldErrors('main_number')">
                        <b-form-group :label="$t('billingAddress.mainNumber')">
                            <b-form-input name="main_number" v-model="billingAddress.main_number" />
                        </b-form-group>
                    </protokol-invalid-feedback>
                </b-col>
            </b-row>

            <protokol-invalid-feedback :errors="validator.fieldErrors('company')">
                <b-form-group :label="$t('billingAddress.companyName')">
                    <b-form-input name="company" v-model="billingAddress.company" />
                </b-form-group>
            </protokol-invalid-feedback>

            <InputField
                type="email"
                name="email"
                :label="$t('email')"
                v-model="billingAddress.email"
                @error="(e) => { fieldError(e, 'billingErrors') }"
                :rules="{required:false, length: {min: 2}}"
            />

            <protokol-invalid-feedback :errors="validator.fieldErrors('address')">
                <b-form-group :label="$t('address')">
                    <b-form-input name="address" v-model="billingAddress.address" />
                </b-form-group>
            </protokol-invalid-feedback>

            <b-row>
                <b-col md="6">
                    <protokol-invalid-feedback :errors="validator.fieldErrors('city')">
                        <b-form-group :label="$t('city')">
                            <b-form-input name="city" v-model="billingAddress.city" />
                        </b-form-group>
                    </protokol-invalid-feedback>
                </b-col>
                <b-col md="3">
                    <InputField
                        name="zipcode"
                        :label="$t('zipcode')"
                        v-model="billingAddress.zipcode"
                        @error="(e) => { fieldError(e, 'billingErrors') }"
                        :rules="{required:false, length: {min: 2}}"
                    />
                </b-col>
                <b-col md="3">
                    <InputField
                        name="state"
                        :label="$t('state')"
                        v-model="billingAddress.state"
                        @error="(e) => { fieldError(e, 'billingErrors') }"
                        :rules="{required:false, length: {min: 2}}"
                    />
                </b-col>
            </b-row>
        </div>

        <b-button type="submit" variant="success" block class="mt-3">
            {{ $t('save') }}
        </b-button>
    </b-form>
</template>
<script>
import Input from "@/components/form/Input"
import Select from "@/components/form/Select"
import Vue from 'vue'
import countries from '@/countries.json'

export default {
    components: {
        InputField: Input,
        SelectField: Select
    },
    data: function() {
        return {
            personBillingErrors: [],
            billingErrors: [],
            countries: null,
            fetchingVatData: false,
            vatError: null,
            typeOptions: [
                {
                    text: this.$t('billingAddress.person'),
                    value: 'person'
                },
                {
                    text: this.$t('billingAddress.company'),
                    value: 'company'
                }
            ],
            billingAddress: null,
            validator: this.$validator()
        }
    },

    watch: {
        billingAddress: {
            handler: function() {
                this.validator.revalidate()
            },

            deep: true
        }
    },

    methods: {
        setBillingType(type) {
            this.$set(this.billingAddress, 'type', type)
        },

        onVatInput(value) {
            const numericValue = value.replace(/\D/g, '')
            this.$set(this.billingAddress, 'vat_number', numericValue)
            this.vatError = null
            if (numericValue.length === 9) {
                this.fetchCompanyData(numericValue)
            }
        },

        async fetchCompanyData(pib) {
            this.fetchingVatData = true
            this.vatError = null
            try {
                const resp = await this.$integrationApi.get('/karadjordje/services/nbs/search?vatNumber=' + pib)
                const info = resp.data
                this.$set(this.billingAddress, 'main_number', info.mb)
                this.$set(this.billingAddress, 'company', info.name)
                this.$set(this.billingAddress, 'address', info.address)
                this.$set(this.billingAddress, 'city', info.place)
                this.validator.revalidate()
            } catch (err) {
                this.vatError = this.$t('companyVatSearchSerbiaError')
            } finally {
                this.fetchingVatData = false
            }
        },

        fieldError(error, errorBag) {
            let bag = errorBag || "errors"
            let errors = this[bag]

            let found = errors.find((i, e) => {
                return i.field == error.field
            })

            if (found === undefined) {
                errors.push(error)
            } else {
                errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },

        checkForErrors(errorBag) {
            let bag = errorBag || "errors"
            let errors = this[bag]

            return errors.find((i, e) => {
                return i.error
            })
        },

        saveBillingInfo(e) {
            e.preventDefault()

            if (!this.validator.pass()) {
                this.$bvToast.toast(this.$t('fieldsNotValid'), {
                    variant: 'danger',
                    title: this.$t('error')
                })

                return
            }

            if (this.password !== this.confirm_password) {
                this.$mainDialog.msgBoxOk(this.$t('passwordNotValid'))

                return
            }

            let model = {
                name: this.user.name,
                email: this.user.email,
                settings: this.settings,
                billing_address: this.billingAddress
            }

            if (this.password != "") {
                model["password"] = this.password
            }

            this.$http.post("/v1/user/profile/edit", model).then(resp => {
                this.$emit('saved', model)

                this.$store.commit("SET_BILLING_ADDRESS", this.billingAddress)
            }).catch(error => {
                if (error.response.status == 500) {
                    this.$serverErrorModal(error)
                    return
                }

                this.$mainDialog.msgBoxOk(error.response.data.message)
            })
        }
    },

    computed: {
        isVatRequired() {
            return true
        },

        user() {
            return this.$store.getters.getUser
        }
    },

    mounted() {
        this.countries = countries.map(d => {
            return d.name.common
        })

        this.billingAddress = this.$store.getters.getUser.settings.billing_address || {}

        this.validator.validate(this.billingAddress, {
            default: (model, errors) => {
                if (!model.country) errors.add('country', [this.$t('fieldRequired')])
                if (!model.address) errors.add('address', [this.$t('fieldRequired')])
                if (!model.city) errors.add('city', [this.$t('fieldRequired')])

                if (model.type === 'company') {
                    if (!model.vat_number) errors.add('vat_number', [this.$t('fieldRequired')])
                    if (!model.main_number && model.country == 'Serbia') errors.add('main_number', [this.$t('fieldRequired')])
                    if (!model.company) errors.add('company', [this.$t('fieldRequired')])
                }

                return errors
            },
        })
    }
}
</script>

<style scoped lang="scss">
.billing-info-form {
    .billing-title {
        font-size: 1.1rem;
        font-weight: 600;
        margin-bottom: 1.5rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .type-toggle {
        display: flex;
        gap: 0.75rem;

        .type-btn {
            flex: 1;
            padding: 0.65rem 1rem;
            border: 1px solid rgba(255, 255, 255, 0.15);
            border-radius: 6px;
            background: transparent;
            color: rgba(255, 255, 255, 0.6);
            font-size: 0.9rem;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                border-color: rgba(255, 255, 255, 0.35);
                color: rgba(255, 255, 255, 0.9);
            }

            &.active {
                border-color: #4dbd74;
                background: rgba(77, 189, 116, 0.1);
                color: #4dbd74;
                font-weight: 500;
            }
        }
    }
}
</style>